import { useSyncExternalStore } from "react";
import type { AskSkill, Attachment, ThreadSubject } from "./model.ts";

/**
 * The ask panel beside the page: one for the tab, opened from anywhere (⌘K, the top bar's ask field, "Ask about
 * this" on any object, Explain this record) without leaving the page. It is not in the address: opening it never
 * navigates, so the page under it stays exactly as it was. The open conversation is remembered for the tab, so a
 * reload opens it again.
 *
 * `openAsk({ text })` asks at once; `openAsk({ about })` opens the panel with the ask box pointed at the object; a
 * bare `openAsk()` opens it on what was open last (or a fresh question).
 */

/** What a question is about, with the words the ask box shows for it ("About Cancellations per week"). */
export type AskAbout = { subject: ThreadSubject; label: string };

/** A question to send as soon as the panel is up. */
export type AskRequest = {
  text: string;
  about?: AskAbout | null;
  skill?: AskSkill;
  attachments?: Attachment[];
  /** Continue this thread instead of starting one. */
  threadId?: string;
};

export type PanelState = {
  open: boolean;
  threadId: string | null;
  about: AskAbout | null;
  /** Bumped by every open, so the ask box takes focus again. */
  opened: number;
  request: AskRequest | null;
  /** Words put in the ask box for the person to finish ("Make a list of "), not sent. */
  draft: string | null;
  /** The box asks for something to build, picked in the palette ("New dashboard"): what's sent runs that skill. */
  mode: AskMode | null;
};

/** Something to build, asked for in the ask box: the skill it runs, the chip's words and the box's placeholder. */
export type AskMode = { skill: AskSkill; label: string; placeholder: string; lead?: string };

const KEY = "adea.ask.thread";

function remembered(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function remember(threadId: string | null) {
  try {
    if (threadId) sessionStorage.setItem(KEY, threadId);
    else sessionStorage.removeItem(KEY);
  } catch {
    /* private window: the panel simply starts fresh after a reload */
  }
}

const PENDING = "adea.ask.pending";

/** The tab reloads onto a newer build (`lib/stale-build.ts`) before the panel could ask: the question waits for it. */
export function keepAskForReload(): void {
  try {
    if (state.request) sessionStorage.setItem(PENDING, JSON.stringify(state.request));
  } catch {
    /* private window: the question is lost with the reload */
  }
}

/** A question kept over a reload, taken once. */
function pending(): AskRequest | null {
  try {
    const kept = sessionStorage.getItem(PENDING);
    sessionStorage.removeItem(PENDING);
    const request = kept ? (JSON.parse(kept) as AskRequest) : null;
    return request && typeof request.text === "string" && request.text.trim() ? request : null;
  } catch {
    return null;
  }
}

const kept = pending();

let state: PanelState = {
  open: !!kept,
  threadId: kept ? (kept.threadId ?? null) : remembered(),
  about: kept?.about ?? null,
  opened: kept ? 1 : 0,
  request: kept,
  draft: null,
  mode: null,
};
const listeners = new Set<() => void>();

function set(next: Partial<PanelState>) {
  state = { ...state, ...next };
  if ("threadId" in next) remember(state.threadId);
  for (const l of listeners) l();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function usePanel(): PanelState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}

export function openAsk(
  opts: {
    text?: string;
    about?: AskAbout;
    skill?: AskSkill;
    attachments?: Attachment[];
    threadId?: string;
    /** Words to put in the box, unsent: a fresh question the person finishes. */
    draft?: string;
  } = {},
) {
  const { text, about, skill, attachments, threadId, draft } = opts;
  const request: AskRequest | null = text?.trim()
    ? {
        text: text.trim(),
        about: about ?? null,
        ...(skill ? { skill } : {}),
        ...(attachments?.length ? { attachments } : {}),
        ...(threadId ? { threadId } : {}),
      }
    : null;
  set({
    open: true,
    opened: state.opened + 1,
    request,
    draft: draft ?? null,
    mode: null,
    // asking about something, or asking anew, starts a fresh conversation; a bare open shows the last one
    ...(about || (request && !threadId) ? { about: about ?? null, threadId: null } : {}),
    ...(threadId ? { threadId } : {}),
    ...(draft !== undefined ? { threadId: null, about: about ?? null } : {}),
  });
}

/**
 * ⌘K and the top bar's ask field: the panel with an empty box, where typing finds places, things and actions or asks
 * Adea. The conversation that was open is the first of the recent ones there.
 */
export const openPalette = () =>
  set({
    open: true,
    threadId: null,
    about: null,
    request: null,
    draft: null,
    mode: null,
    opened: state.opened + 1,
  });

/** Shows a conversation in the panel. */
export const showThread = (threadId: string) =>
  set({ open: true, threadId, about: null, request: null, mode: null, opened: state.opened + 1 });

/** A fresh question: the panel stays open, empty. */
export const newQuestion = () =>
  set({ threadId: null, about: null, request: null, mode: null, opened: state.opened + 1 });

/** The ask box asks for something to build (a fresh question, with the mode's chip and placeholder). */
export const askFor = (mode: AskMode) =>
  set({
    open: true,
    threadId: null,
    about: null,
    request: null,
    draft: null,
    mode,
    opened: state.opened + 1,
  });

export const clearMode = () => set({ mode: null });

export const closeAsk = () => set({ open: false, request: null });

/** The request was taken (sent); the panel now shows its thread. */
export const requestTaken = (threadId: string) => set({ request: null, threadId, mode: null });

export const clearAbout = () => set({ about: null });
